import { useCallback, useEffect, useState } from 'react';
import { createEmptyCondition, normalizeInitialData } from '../utils/normalizeInitialData.utils';

const useActionBuilderState = (initialData) => {
  const [builderState, setBuilderState] = useState(() => normalizeInitialData(initialData)); // Lazy initialization (only runs on first render)
  const [hasTouchedUntil, setHasTouchedUntil] = useState(() =>
    Boolean(initialData?.resolutions?.length),
  );

  useEffect(() => {
    setBuilderState(normalizeInitialData(initialData));
    setHasTouchedUntil(Boolean(initialData?.resolutions?.length));
  }, [initialData]);

  const updateMetadata = useCallback((newFields) => {
    setBuilderState((prevState) => ({
      ...prevState,
      metadata: {
        ...prevState.metadata,
        ...newFields,
      },
    }));
  }, []);

  const addConditionGroup = useCallback(() => {
    setBuilderState((prevState) => ({
      ...prevState,
      conditions: [...prevState.conditions, [createEmptyCondition()]],
    }));
  }, []);

  const removeConditionGroup = useCallback((groupIndex) => {
    setBuilderState((prevState) => {
      const conditions = [...prevState.conditions];

      if (!conditions[groupIndex]) {
        console.error(
          `Group index ${groupIndex} does not exist. Cannot remove non-existent group.`,
        );
        return prevState;
      }

      conditions.splice(groupIndex, 1); // Remove the group at the specified index
      return {
        ...prevState,
        conditions: conditions.length ? conditions : [[createEmptyCondition()]],
      };
    });
  }, []);

  const addCondition = useCallback((condition, groupIndex) => {
    setBuilderState((prevState) => {
      const conditions = [...prevState.conditions];

      if (!conditions[groupIndex]) {
        console.error(
          `Group index ${groupIndex} does not exist. Please add the condition group before adding conditions to it.`,
        );
        return prevState;
      }

      conditions[groupIndex] = [...conditions[groupIndex], condition];
      return {
        ...prevState,
        conditions,
      };
    });
  }, []);

  const updateCondition = useCallback((id, updates = {}) => {
    setBuilderState((prevState) => ({
      ...prevState,
      conditions: prevState.conditions.map((group) =>
        group.map((condition) =>
          condition.id === id
            ? {
                ...condition,
                ...updates,
                formData: updates.formData !== undefined ? updates.formData : condition.formData,
              }
            : condition,
        ),
      ),
    }));
  }, []);

  const removeCondition = useCallback((id) => {
    setBuilderState((prevState) => {
      const conditions = prevState.conditions
        .map((conditionGroup) => conditionGroup.filter((condition) => condition.id !== id)) // Remove condition from group
        .filter((conditionGroup) => conditionGroup.length > 0); // Remove empty groups

      return {
        ...prevState,
        conditions: conditions.length ? conditions : [[createEmptyCondition()]],
      };
    });
  }, []);

  const addAction = useCallback((action) => {
    setBuilderState((prevState) => ({
      ...prevState,
      actions: [...prevState.actions, action],
    }));
  }, []);

  const updateAction = useCallback((id, updates) => {
    setBuilderState((prevState) => ({
      ...prevState,
      actions: prevState.actions.map((action) =>
        action.id === id
          ? {
              ...action,
              ...updates,
              formData: updates.formData !== undefined ? updates.formData : action.formData,
            }
          : action,
      ),
    }));
  }, []);

  const removeAction = useCallback((id) => {
    setBuilderState((prevState) => ({
      ...prevState,
      actions: prevState.actions.filter((action) => action.id !== id),
    }));
  }, []);

  const addUntilGroup = useCallback(() => {
    setHasTouchedUntil(true);
    setBuilderState((prevState) => ({
      ...prevState,
      until: [...prevState.until, [createEmptyCondition()]],
    }));
  }, []);

  const removeUntilGroup = useCallback(
    (groupIndex) => {
      setBuilderState((prevState) => {
        const until = [...prevState.until];

        if (!until[groupIndex]) {
          console.error(
            `Group index ${groupIndex} does not exist. Cannot remove non-existent group.`,
          );
          return prevState;
        }

        until.splice(groupIndex, 1);

        return {
          ...prevState,
          until: until.length ? until : [],
        };
      });
    },
    [],
  );

  const addUntil = useCallback((condition, groupIndex) => {
    setHasTouchedUntil(true);
    setBuilderState((prevState) => {
      const until = [...prevState.until];

      if (!until[groupIndex]) {
        console.error(
          `Group index ${groupIndex} does not exist. Please add the group before adding conditions.`,
        );
        return prevState;
      }

      until[groupIndex] = [...until[groupIndex], condition];

      return {
        ...prevState,
        until,
      };
    });
  }, []);

  const updateUntil = useCallback((id, updates) => {
    setBuilderState((prevState) => ({
      ...prevState,
      until: prevState.until.map((conditionGroup) =>
        conditionGroup.map((condition) =>
          condition.id === id
            ? {
                ...condition,
                ...updates,
                formData: updates.formData !== undefined ? updates.formData : condition.formData,
              }
            : condition,
        ),
      ),
    }));
  }, []);

  const removeUntil = useCallback(
    (id) => {
      setBuilderState((prevState) => {
        const until = prevState.until
          .map((conditionGroup) => conditionGroup.filter((condition) => condition.id !== id)) // Remove condition from group
          .filter((conditionGroup) => conditionGroup.length > 0); // Remove empty groups from until

        return {
          ...prevState,
          until: until.length ? until : [],
        };
      });
    },
    [],
  );

  return {
    builderState,
    updateMetadata,
    addConditionGroup,
    removeConditionGroup,
    addCondition,
    updateCondition,
    removeCondition,
    addAction,
    updateAction,
    removeAction,
    addUntilGroup,
    removeUntilGroup,
    addUntil,
    updateUntil,
    removeUntil,
  };
};

export default useActionBuilderState;
